/**
 * <XtsAreas> — renders configured AreaConfig[].
 *
 * Areas are the stop-position-free sibling of Stations: a coloured tube
 * along [start..end] of one or more parts, with an optional description
 * billboard at the tube's midpoint. No stop markers, no per-station
 * marker overrides. Use them to mark zones (cleanroom, safety area,
 * manual access, …) that don't drive any mover behaviour.
 *
 * Geometry / placement / multi-part semantics mirror <XtsStations> 1:1 so
 * the two overlays read consistently when both are visible at once.
 */

import React, { useMemo } from 'react';
import type { BuiltChain } from '../../geometry/ChainBuilder.js';
import {
  sampleChainPoint,
  sampleChainRange,
} from '../../geometry/sampleChain.js';
import {
  chainEndpointsForPart,
  userToChainPositionMm,
} from '../../geometry/positionFrame.js';
import { PathTube } from './PathTube.js';
import { DimensionLabel } from './DimensionLabel.js';
import { PartTransformGroup } from './PartTransformGroup.js';
import type {
  AreaConfig,
  DisplayOptions,
  PartTransformation,
  PositionFrame,
} from '../../types.js';
import { argbIntToHex } from './XtsStations.js';

interface XtsAreasProps {
  areas: AreaConfig[];
  chainsByPartOid: Map<number, BuiltChain>;
  /** Per-part transforms keyed by `PartConfig.globalNumber`. */
  partTransformsByPartOid?: Map<number, PartTransformation | undefined>;
  display?: DisplayOptions;
  /** XPU position frame applied to start / end. */
  positionFrame?: PositionFrame;
}

const XtsAreasImpl: React.FC<XtsAreasProps> = ({
  areas,
  chainsByPartOid,
  partTransformsByPartOid,
  display,
  positionFrame,
}) => {
  const opts = display?.areaOptions;
  const thickness = opts?.thicknessMm ?? 8;
  const liftZ = opts?.displacementMm ?? 60;
  const lateralMm = opts?.lateralDisplacementMm ?? 0;
  const showDescription = opts?.showAreaDescription ?? true;
  const labelSize = opts?.textOptions?.sizeMm ?? 30;
  const labelColorOverride = opts?.labelColorOverride;
  const fontFamily = opts?.textOptions?.fontFamily;
  const opacity = opts?.opacity ?? 1;

  return (
    <group name="xts-areas">
      {areas
        .filter((a) => a.isEnabled)
        .map((a) => {
          const anchorOid = a.partOids[0];
          const partTransform =
            anchorOid !== undefined
              ? partTransformsByPartOid?.get(anchorOid)
              : undefined;
          return (
            <PartTransformGroup
              key={a.areaId}
              name={`area-wrap-${a.areaId}`}
              partTransformation={partTransform}
            >
              <Area
                area={a}
                chainsByPartOid={chainsByPartOid}
                thickness={thickness}
                liftZ={liftZ}
                lateralMm={lateralMm}
                opacity={opacity}
                showDescription={showDescription}
                labelSize={labelSize}
                labelColorOverride={labelColorOverride}
                fontFamily={fontFamily}
                positionFrame={positionFrame}
              />
            </PartTransformGroup>
          );
        })}
    </group>
  );
};

export const XtsAreas = React.memo(XtsAreasImpl);
XtsAreas.displayName = 'XtsAreas';

const Area: React.FC<{
  area: AreaConfig;
  chainsByPartOid: Map<number, BuiltChain>;
  thickness: number;
  liftZ: number;
  lateralMm: number;
  opacity: number;
  showDescription: boolean;
  labelSize: number;
  labelColorOverride?: string;
  fontFamily?: string;
  positionFrame?: PositionFrame;
}> = ({
  area,
  chainsByPartOid,
  thickness,
  liftZ,
  lateralMm,
  opacity,
  showDescription,
  labelSize,
  labelColorOverride,
  fontFamily,
  positionFrame,
}) => {
  const colorHex = useMemo(() => argbIntToHex(area.color), [area.color]);
  const labelColor = labelColorOverride ?? colorHex;

  // Multi-part: identical logic to <XtsStations>. With a single partOid and
  // end<start we wrap around the track end (= closed-loop area).
  const segments = useMemo(() => {
    const parts =
      area.partOids.length === 1 &&
      area.endPositionOnPart < area.startPositionOnPart
        ? [area.partOids[0]!, area.partOids[0]!]
        : area.partOids;

    const out: Array<{ chain: BuiltChain; start: number; end: number }> = [];
    for (let i = 0; i < parts.length; i++) {
      const chain = chainsByPartOid.get(parts[i]!);
      if (!chain) continue;
      const isFirst = i === 0;
      const isLast = i === parts.length - 1;
      // Same direction-aware part-spanning rule as <XtsStations>: for
      // intermediate parts the segment covers the entire chain in the
      // user's "forward" direction, so direction='negative' lands on
      // chain-frame trackLengthMm → 0 instead of overshooting.
      const ep = chainEndpointsForPart(chain.trackLengthMm, positionFrame);
      const start = isFirst
        ? userToChainPositionMm(area.startPositionOnPart, positionFrame)
        : ep.userStartChainMm;
      const end = isLast
        ? userToChainPositionMm(area.endPositionOnPart, positionFrame)
        : ep.userEndChainMm;
      out.push({ chain, start, end });
    }
    return out;
  }, [area, chainsByPartOid, positionFrame]);

  const descriptionAnchor = useMemo(() => {
    if (!showDescription || segments.length === 0) return null;
    const seg = segments[0]!;
    const mid = (seg.start + seg.end) / 2;
    return sampleChainPoint(seg.chain, mid, liftZ, lateralMm);
  }, [showDescription, segments, liftZ, lateralMm]);

  return (
    <group name={`area-${area.areaId}`}>
      {segments.map((seg, i) => {
        const points = sampleChainRange(
          seg.chain,
          seg.start,
          seg.end,
          20,
          liftZ,
          lateralMm,
        );
        return (
          <PathTube
            key={i}
            points={points}
            thicknessMm={thickness}
            color={colorHex}
            opacity={opacity}
          />
        );
      })}

      {descriptionAnchor && area.description && (
        <DimensionLabel
          positionMm={[
            descriptionAnchor.position.x,
            descriptionAnchor.position.y,
            descriptionAnchor.position.z,
          ]}
          text={area.description}
          sizeMm={labelSize}
          color={labelColor}
          fontFamily={fontFamily}
          liftMm={thickness * 1.5 + labelSize * 0.3}
        />
      )}
    </group>
  );
};
